/**
 * Player State Management
 *
 * Manages dynamic UUID-based player and ship IDs with localStorage persistence.
 * Ensures consistent IDs across session until reset.
 */

const PLAYER_ID_KEY = 'space-antics-player-id';
const SHIP_ID_KEY = 'space-antics-ship-id';

/**
 * Generate a UUID (using crypto.randomUUID if available, fallback to timestamp-based)
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Player State Manager
 * Handles generation, storage, and retrieval of player and ship UUIDs
 */
export class PlayerStateManager {
  /**
   * Get the current player ID from localStorage, or generate a new one
   */
  static getPlayerId(): string {
    try {
      const stored = localStorage.getItem(PLAYER_ID_KEY);
      if (stored) {
        return stored;
      }
    } catch (error) {
      console.warn('Failed to read player ID from localStorage:', error);
    }

    // Generate new player ID
    const playerId = `player-${generateUUID()}`;
    this.setPlayerId(playerId);
    return playerId;
  }

  /**
   * Get the current ship ID from localStorage, or generate a new one
   */
  static getShipId(): string {
    try {
      const stored = localStorage.getItem(SHIP_ID_KEY);
      if (stored) {
        return stored;
      }
    } catch (error) {
      console.warn('Failed to read ship ID from localStorage:', error);
    }

    // Generate new ship ID
    const shipId = `ship-${generateUUID()}`;
    this.setShipId(shipId);
    return shipId;
  }

  /**
   * Set the player ID in localStorage
   */
  static setPlayerId(playerId: string): void {
    try {
      localStorage.setItem(PLAYER_ID_KEY, playerId);
    } catch (error) {
      console.error('Failed to save player ID to localStorage:', error);
    }
  }

  /**
   * Set the ship ID in localStorage
   */
  static setShipId(shipId: string): void {
    try {
      localStorage.setItem(SHIP_ID_KEY, shipId);
    } catch (error) {
      console.error('Failed to save ship ID to localStorage:', error);
    }
  }

  /**
   * Generate new player and ship UUIDs and store them
   * Used during reset/initialization
   */
  static generateNewIds(): { playerId: string; shipId: string } {
    const playerId = `player-${generateUUID()}`;
    const shipId = `ship-${generateUUID()}`;

    this.setPlayerId(playerId);
    this.setShipId(shipId);

    return { playerId, shipId };
  }

  /**
   * Clear player and ship IDs from localStorage
   * Used during full reset
   */
  static clearIds(): void {
    try {
      localStorage.removeItem(PLAYER_ID_KEY);
      localStorage.removeItem(SHIP_ID_KEY);
    } catch (error) {
      console.error('Failed to clear IDs from localStorage:', error);
    }
  }

  /**
   * Check if player ID exists in localStorage
   */
  static hasPlayerId(): boolean {
    try {
      return localStorage.getItem(PLAYER_ID_KEY) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if ship ID exists in localStorage
   */
  static hasShipId(): boolean {
    try {
      return localStorage.getItem(SHIP_ID_KEY) !== null;
    } catch (error) {
      return false;
    }
  }
}
